import {
  Divider,
  DropdownButtonContext,
  IconButton,
  SelectionList,
  SelectionListChangeEvent,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { Fragment, memo, useCallback, useContext, useRef } from 'react';
import { useMount } from 'react-use';

import {
  CopilotAction,
  CopilotDropdownActionContext,
  CopilotDropdownActionParams,
} from 'components/copilotDropdown';
import { useCommands } from 'utils/commands';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useServerStorage } from 'utils/useServerStorage';

import { CopilotSelectionListItemContent } from './components';
import styles from './copilotSelectionList.module.scss';

type CopilotSelectionListProps = {
  readonly onChange: (actionName: string) => void;
};

/** Represents a {@link SelectionList} containing copilot actions. */
export const CopilotSelectionList = memo(function CopilotSelectionList({
  onChange,
}: CopilotSelectionListProps): JSX.Element {
  const { hasCopilot } = useCopilotTrial();
  const { set } = useServerStorage('user');
  const { actions, featureFlags, actionsComputed } = useContext(
    CopilotDropdownActionContext,
  );
  const dropdownContext = useContext(DropdownButtonContext);
  const { keybindings } = useCommands();
  const selectionListRef = useRef<HTMLUListElement>(null);

  const handleChange = useCallback(
    (event: SelectionListChangeEvent<string>): void => {
      ampli.agentCopilotDropdownItemClicked({
        key: event.selectedItem.value,
      });
      onChange(event.selectedItem.value);
    },
    [onChange],
  );

  useMount(() => {
    requestAnimationFrame(() => {
      selectionListRef.current?.focus();
    });
  });

  const params: CopilotDropdownActionParams = {
    dropdownContext,
    featureFlags,
    hasCopilot,
  };

  return (
    <SelectionList<string> onChange={handleChange} ref={selectionListRef}>
      {Object.entries(actions).map((entry, index) => {
        const name = entry[0] as string;
        const action = entry[1] as CopilotAction;
        const { icon, title, commandName, hasDividerBefore } = action;
        const actionComputed = actionsComputed[name];

        const isVisible: boolean = actionComputed.isVisible ?? true;
        const isDisabled: string | boolean = actionComputed.isDisabled ?? false;
        const isPinned = actionComputed.isPinned;

        if (!isVisible) {
          return null;
        }

        const togglePinned = (e) => {
          e.stopPropagation();
          set({
            agent_copilot: (draft) => {
              if (!draft.pinned) {
                draft.pinned = {};
              }
              draft.pinned[name] = !isPinned;
            },
          });
        };

        const item = (
          <Tooltip
            key={`${index}-item`}
            content={isDisabled}
            disabled={typeof isDisabled !== 'string'}
          >
            <div className={styles.container}>
              <SelectionList.Item
                value={name}
                noFixedSelectionStateSpace
                isDisabled={!!isDisabled}
                className={styles.item}
              >
                <CopilotSelectionListItemContent
                  icon={icon}
                  title={typeof title === 'string' ? title : title(params)}
                  keyCombinations={commandName && keybindings[commandName]}
                  actionName={name}
                />
              </SelectionList.Item>
              {isPinned !== undefined && (
                <IconButton
                  icon={isPinned ? 'thumbtack-full' : 'thumbtack'}
                  variant="tertiary"
                  iconSize={16}
                  onClick={togglePinned}
                  className={styles.pinButton}
                  tabIndex={-1}
                />
              )}
            </div>
          </Tooltip>
        );

        if (!hasDividerBefore) {
          return item;
        }

        return (
          <Fragment key={`${index}-fragment`}>
            <Divider />

            {item}
          </Fragment>
        );
      })}
    </SelectionList>
  );
});
